import {DecryptMessageResult, Key, KeyID, Message, VerifyMessageResult} from "openpgp";
import {Base64} from "js-base64";
import {decodeMimePartContent, MimeParser, MimePart} from "../parsers/MimeParser";
import {extractEmailAddresses} from "../util/Formatters";
import {ContentType} from "../util/ContentTypes";
import {
    decryptSecretKeys,
    findDecryptionKeys,
    getPgpSigningKey,
    pgpDecrypt,
    pgpReadArmoredMessage,
    pgpReadBinaryMessage,
    pgpReadCleartextMessage,
    pgpReadKey,
    pgpReadTextSignature,
    pgpSign,
    pgpSignAndEncrypt,
    pgpTextMessage,
    pgpVerify,
} from "../util/PGPUtil";
import {getKeyHexId, getKeyUserIds, PgpKey} from "../domain/PgpKey";
import DbDraftEmail, {EmailMimeContent, SendEmailDto} from "../domain/DbDraftEmail";
import {AccountsState} from "../state/AccountState";
import {findGatekeeperSecretKey} from "../util/GateKeeperXmlParser";
import {createTrackingImage, replaceImageUrlsWithContentIds} from "../util/MimeUtil";
import {DbAttachment} from "../db/DbSchema";
import {MimeWriterPart} from "../parsers/MimeWriter";
import {uuid} from "../util/Uuid";
import {addAttachmentsToMimePart, createMimeEmailContent} from "../util/EmailFactory";

export interface SignatureState {
    verified: boolean;
    users: string[];
}

export interface DecryptedEmail {
    bestPart: MimePart;
    subject?: string;
    signatures: SignatureState[];
    errors: string[];
    attachments: MimePart[];
}

export function checkDraftEmailEncryptionRecipients(draftEmail: DbDraftEmail | undefined, publicKeys: PgpKey[]): string[] {
    if (!draftEmail) return [];

    const addresses = [
        ...extractEmailAddresses(draftEmail.To),
        ...extractEmailAddresses(draftEmail.Cc),
        ...extractEmailAddresses(draftEmail.Bcc)
    ];

    if (publicKeys.length === 0) return addresses;

    const allKeyAddresses = publicKeys.flatMap(pk => getKeyUserIds(pk).flatMap(extractEmailAddresses));

    // console.log(allKeyAddresses);
    return addresses
        .filter(address => !allKeyAddresses.includes(address));
}

export function findEmailEncryptionRecipientKeys(draftEmail: DbDraftEmail | undefined, publicKeys: PgpKey[]): PgpKey[] {
    if (!draftEmail) return [];

    const addresses = [
        ...extractEmailAddresses(draftEmail.To),
        ...extractEmailAddresses(draftEmail.Cc),
        ...extractEmailAddresses(draftEmail.Bcc)
    ];

    if (publicKeys.length === 0) return [];

    return publicKeys.filter(pk => addresses.some(a => getKeyUserIds(pk).flatMap(extractEmailAddresses).includes(a)));
}

export function findEmailEncryptionSenderKey(draftEmail: DbDraftEmail, secretKeys: PgpKey[]): PgpKey[] {
    const addresses = extractEmailAddresses(draftEmail.From);

    const foundKeys = findGatekeeperSecretKey(secretKeys, addresses);

    if (foundKeys.length === 0) {
        throw new Error("Unable to encrypt/sign the email as you don't have a key");
    }

    return foundKeys;
}

export interface DraftEmailPasswordRequired {
    message: string | undefined,
    key: PgpKey,
}

function normaliseMessageId(messageId: string): string {
    messageId = messageId.trim();
    if (!messageId.startsWith("<")) {
        return `<${messageId}>`;
    }
    return messageId;
}

// TODO: move this somewhere more sensible!
export async function prepareDraftEmail(draftEmail: DbDraftEmail, attachments: DbAttachment[], {
    password,
    pgpKeys,
    secretKeys,
}: AccountsState): Promise<SendEmailDto | DraftEmailPasswordRequired> {
    const TrackingImageId = "CLIENT-" + uuid();
    const content: EmailMimeContent = {
        VerbatimHeaders: {},
        VerbatimContentData: "",
        TrackingImageId,
    };
    let Subject = draftEmail.Subject;

    if (draftEmail.ReplyToEmail) {
        content.VerbatimHeaders["In-Reply-To"] = normaliseMessageId(draftEmail.ReplyToEmail);
    }
    if (draftEmail.ForwardEmail) {
        content.VerbatimHeaders["X-Forwarded-Message-Id"] = normaliseMessageId(draftEmail.ForwardEmail);
    }

    if (draftEmail.IsSigned && !draftEmail.IsEncrypted) {
        if (attachments.length > 0) {
            // TODO: support attachments in signed emails
            throw Error("Signing emails with attachments isn't currently supported");
        }
        const signingKeys = findEmailEncryptionSenderKey(draftEmail, secretKeys);
        console.log("Signing keys", signingKeys);
        // TODO: see why there can be multiple keys here

        const trackingImage = createTrackingImage(TrackingImageId);
        const fullText = replaceImageUrlsWithContentIds(draftEmail.BodyText + (draftEmail.ExtendedBodyText ?? "")) + trackingImage;

        try {
            const rootPart = new MimeWriterPart(ContentType.PgpMultipartSigned, undefined, "This is a cryptographically signed message in MIME format.", uuid());
            rootPart.protocol = ContentType.PgpSignature;

            const base64Message = btoa(fullText);
            const messagePart = MimeWriterPart.createInlinePart(ContentType.HtmlTextUtf8, "base64", base64Message);
            rootPart.addSubPart(messagePart);

            const signingKey = await getPgpSigningKey(signingKeys, password);

            // TODO: use binary format here instead of a string?
            const messagePartString = await messagePart.partAsString();
            const messageToSign = await pgpTextMessage(messagePartString);
            const armouredSignature = await pgpSign(messageToSign, signingKey, true);

            const signaturePart = MimeWriterPart.createAttachmentPart(ContentType.PgpSignature, "7bit", "signature.txt", armouredSignature, null);
            rootPart.addSubPart(signaturePart);

            content.VerbatimHeaders["Content-Type"] = rootPart.getContentType();
            content.VerbatimContentData = await rootPart.asString();
        } catch (e) {
            if (e?.toString().includes("Incorrect key passphrase")) {
                return {
                    message: e?.toString(),
                    key: signingKeys[0],
                };
            }
            throw e;
        }
    }

    if (draftEmail.IsEncrypted) {
        const trackingImage = createTrackingImage(TrackingImageId);
        const fullText = replaceImageUrlsWithContentIds(draftEmail.BodyText + (draftEmail.ExtendedBodyText ?? "")) + trackingImage;

        const signingKeys = findEmailEncryptionSenderKey(draftEmail, secretKeys);
        console.log("Signing keys", signingKeys);
        const boundary = uuid();
        const rootPart = new MimeWriterPart(ContentType.MultipartEncrypted, [], "This is an OpenPGP/MIME encrypted message (RFC 4880 and 3156)", boundary);//`multipart/encrypted;\r\n\tprotocol="application/pgp-encrypted"`, );
        rootPart.protocol = "application/pgp-encrypted";

        const versionPart = new MimeWriterPart(ContentType.PgpEncrypted, [{type: "Content-Description", value: "PGP/MIME version identification"}], "Version: 1")
        rootPart.addSubPart(versionPart);

        const errors = checkDraftEmailEncryptionRecipients(draftEmail, pgpKeys);
        if (errors.length > 0) {
            throw new Error("Unable to encrypt for these recipients: " + errors.join("; "));
        }

        try {
            const plainTextParts = new MimeWriterPart(`multipart/mixed;\r\n\tprotected-headers="v1"`, [{
                type: "Subject",
                value: MimeWriterPart.encodeIfRequired(draftEmail.Subject)
            }], "", uuid());

            const plainTextMessagePart = MimeWriterPart.createInlinePart(ContentType.HtmlTextUtf8, "base64", btoa(fullText));
            plainTextParts.addSubPart(plainTextMessagePart);

            await addAttachmentsToMimePart(plainTextParts, attachments);

            Subject = "..."; // Hide the email subject - included in encrypted content

            const recipientKeys = findEmailEncryptionRecipientKeys(draftEmail, pgpKeys);
            recipientKeys.push(...findEmailEncryptionSenderKey(draftEmail, secretKeys));

            const signingKey = await getPgpSigningKey(signingKeys, password);

            // TODO: use binary format here instead of a string?
            const plainTextMessage = await pgpTextMessage(await plainTextParts.partAsString());
            const encryptedMessage = await pgpSignAndEncrypt(plainTextMessage, recipientKeys.map(k => k.key), signingKey);

            const encryptedMessagePart = new MimeWriterPart("application/octet-stream; name=\"encrypted.asc\"", [
                {type: "Content-Description", value: "OpenPGP encrypted message"},
                {type: "Content-Disposition", value: "inline; filename=\"encrypted.asc\""},
            ], encryptedMessage.replaceAll("\n", "\r\n"));

            rootPart.addSubPart(encryptedMessagePart);

            content.VerbatimHeaders["Content-Type"] = rootPart.getContentType();
            content.VerbatimContentData = await rootPart.asString();
        } catch (e) {
            if (e?.toString().includes("Incorrect key passphrase")) {
                return {
                    message: e?.toString(),
                    key: signingKeys[0],
                };
            }
            throw e;
        }
    }
    if (!draftEmail.IsSigned && !draftEmail.IsEncrypted) {
        await createMimeEmailContent(draftEmail, content, attachments);
    }
    return {
        ...content,
        IsDraft: false,
        ThreadId: draftEmail.ThreadId,
        Uid: draftEmail.Uid,
        Subject,
        Id: -1,
        EditUid: draftEmail.EditUid,
        From: draftEmail.From,
        To: draftEmail.To,
        Cc: draftEmail.Cc,
        Bcc: draftEmail.Bcc,
        IsSigned: draftEmail.IsSigned,
        IsEncrypted: draftEmail.IsEncrypted,
    };
}

async function getEncryptedMessageFromContent(encryptedContentString: string, contentType: string): Promise<Message<string | Uint8Array>> {
    if (contentType === ContentType.PgpBinary) {
        console.log("Decrypting binary message");
        const encryptedData = Base64.toUint8Array(encryptedContentString);
        return await pgpReadBinaryMessage(encryptedData);
    } else {
        return await pgpReadArmoredMessage(encryptedContentString);
    }
}

export async function findEmailDecryptionKeys(encryptedContentString: string, contentType: string, secretKeys: PgpKey[]): Promise<PgpKey[]> {
    try {
        const binaryMessage = await getEncryptedMessageFromContent(encryptedContentString, contentType);

        return findDecryptionKeys(binaryMessage, secretKeys);
    } catch (e) {
        console.error(e);
    }

    return [];
}

export async function decryptEncryptedEmail(encryptedContentString: string, contentType: string, secretKeys: PgpKey[], publicKeys: Key[], password: string): Promise<DecryptedEmail> {

    if (secretKeys.length === 0) {
        throw new Error("You don't have a key that can decrypt this email");
    }
    if (password.length === 0) {
        throw new Error("Please enter your password");
    }

    const binaryMessage = (await getEncryptedMessageFromContent(encryptedContentString, contentType)) as Message<Uint8Array>;

    console.log(`Encrypted to keys: ${binaryMessage.getEncryptionKeyIDs().map((k: KeyID) => k.toHex()).join(", ")}`);

    const decryptedKeys = await decryptSecretKeys(secretKeys, password);

    console.log(`Decrypted keys: ${decryptedKeys.map(getKeyHexId).join(", ")}`);

    const decryptedMessage = await pgpDecrypt(binaryMessage, decryptedKeys, publicKeys);
    const decryptedData = decryptedMessage.data;

    const parser = new MimeParser(decryptedData);
    parser.parse();

    const attachments: MimePart[] = parser.getAttachmentParts();
    const errors: string[] = [];
    let signatureStates: SignatureState[];

    try {
        const allKeys = [...publicKeys];

        const keyPart = parser.findExactPart("application/pgp-keys");
        if (keyPart) {
            try {
                const verificationKey = await pgpReadKey({
                    armoredKey: keyPart.contentLines.join("\n"),
                    checksumRequired: false
                });
                allKeys.push(verificationKey);
            } catch (e) {
                // console.log("Key: ", parser.findExactPart("application/pgp-keys")?.contentLines.join("\r\n"));
            }
        }

        const detachedSignaturePart = parser.findExactPart("application/pgp-signature");
        const messagePart = parser.findExactPart("multipart/mixed");
        if (detachedSignaturePart && messagePart && keyPart) {
            const result = await verifyParsedEmailSignatures(parser, publicKeys);
            signatureStates = result.signatures;
            errors.push(...result.errors);
        } else {
            signatureStates = await convertSignatures(decryptedMessage, publicKeys);
            console.log("Signatures: ", signatureStates);
        }
    } catch (e) {
        console.log("Verification error:", e);
        errors.push("Unable to verify signatures: " + e);
        signatureStates = [];
    }
    const bestPart = parser.findBestPart(ContentType.HtmlText);

    return {
        bestPart: bestPart,
        signatures: signatureStates,
        subject: parser.subject,
        errors,
        attachments,
    };
}

export async function verifyEmailSignatures(content: string, publicKeys: Key[]): Promise<DecryptedEmail> {
    const parser = new MimeParser(content);
    parser.parse();

    return verifyParsedEmailSignatures(parser, publicKeys);
}

export async function verifyParsedEmailSignatures(parser: MimeParser, publicKeys: Key[]): Promise<DecryptedEmail> {
    let bestPart = parser.findBestPart(ContentType.HtmlText);
    //const signaturePart = parser.findBestPart("application/octet-stream");

    let signatures: SignatureState[] = [];

    const errors: string[] = [];

    try {
        const bestPartContent = decodeMimePartContent(bestPart);
        if (bestPartContent.includes("-----BEGIN PGP SIGNED MESSAGE-----")) {
            // Inline signature
            // TODO: check if we need this...
            // bestPartContent = bestPart.content.includes("-----END PGP SIGNED MESSAGE-----") ? bestPart.content : bestPart.content + "\r\n-----END PGP SIGNED MESSAGE-----";

            const messageToVerify = await pgpReadCleartextMessage(bestPartContent);
            const verifiedMessageResult = await pgpVerify(messageToVerify as unknown as Message<any>, publicKeys, undefined);

            // TODO: check if we need this...
            // bestPart.content = data as unknown as string;

            signatures = await convertSignatures(verifiedMessageResult, publicKeys);

            // console.log("Signatures: ", signatures);
            // return {signatures, bestPart};
        } else {
            // TODO: Support detached signatures
            const signaturePart = parser.findBestPart(ContentType.PgpSignature);
            if (signaturePart.contentType !== ContentType.PgpSignature) {
                console.error("Signature part not found");
            } else {
                const signedDataPart = parser.findBestPart("multipart/signed").subParts[0];
                // TODO: detect if the signature is armored
                // const binarySignature = Base64.toUint8Array(signaturePart.content);
                const signature = await pgpReadTextSignature(decodeMimePartContent(signaturePart));

                try {
                    const signedData = parser.getMimePartLines(signedDataPart).join("\r\n");
                    // console.log("Signed data:\r\n", signedData);

                    const messageToVerify = await pgpTextMessage(signedData);
                    const verifyMessageResult = await pgpVerify(messageToVerify as unknown as Message<any>, publicKeys, signature);

                    bestPart = parser.findBestPart(ContentType.HtmlText);

                    signatures = await convertSignatures(verifyMessageResult, publicKeys);

                    // TODO: read in the headers from the signed part of the message

                } catch (e: any) {
                    console.info("Unable to verify email: ", e);
                    errors.push("Unable to verify email: " + e);
                    signatures = [{users: [e.message], verified: false}];
                }
            }
        }
    } catch (e: any) {
        console.info("Unable to verify email: ", e);
        errors.push("Unable to verify email: " + e);
    }

    console.log("Signatures: ", signatures);

    return {
        signatures,
        bestPart,
        errors,
        attachments: [],
    };
}

async function convertSignatures(messageResult: DecryptMessageResult | VerifyMessageResult, publicKeys: Key[]) {
    const {signatures} = messageResult;
    const signatureStates: SignatureState[] = [];
    for (const signature of signatures) {
        const publicKey = publicKeys.find(pk => pk.getKeyID().equals(signature.keyID));
        if (publicKey) {
            try {
                const verified = await signature.verified;
                signatureStates.push({
                    verified: !!verified,
                    users: extractEmailAddresses(publicKey.getUserIDs().join(";")),
                });
            } catch (e: any) {
                console.error("Unable to verify email signature: ", e);
                signatureStates.push({
                    verified: false,
                    users: extractEmailAddresses(publicKey.getUserIDs().join(";")),
                });
            }
        } else {
            const hexId = signature.keyID.toHex();
            console.info(`Signing key not found: ${hexId}`);
            signatureStates.push({verified: false, users: [hexId]});
        }
    }
    return signatureStates;
}
